import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  saludos,
  scoreAccionesSaludos,
  reglasSaludos,
  globalStatsSaludos,
  tipoLabels,
  estadoLabelsSaludo,
  destinoLabels,
  getMasCompartidos,
  getMayorScore,
  getMasRecientes,
  getSaludosPorEstado,
} from '@/mocks/sistemaSaludos';
import type { EstadoSaludo, TipoSaludo, DestinoSaludo } from '@/mocks/sistemaSaludos';

type TabSaludos = 'al-aire' | 'mas-compartidos' | 'destacados' | 'acciones' | 'reglas';

export default function SistemaSaludosSection() {
  const [activeTab, setActiveTab] = useState<TabSaludos>('al-aire');
  const [tipoFiltro, setTipoFiltro] = useState<TipoSaludo | 'todos'>('todos');
  const [destinoFiltro, setDestinoFiltro] = useState<DestinoSaludo | 'todos'>('todos');

  const alAire = getSaludosPorEstado('al_aire');
  const destacados = getSaludosPorEstado('destacado');
  const masCompartidos = getMasCompartidos().slice(0, 6);
  const mayorScore = getMayorScore().slice(0, 6);

  const tipoPills: { tipo: TipoSaludo | 'todos'; label: string; icono: string }[] = [
    { tipo: 'todos', label: 'Todos', icono: 'ri-filter-line' },
    { tipo: 'texto', label: 'Texto', icono: 'ri-text' },
    { tipo: 'audio', label: 'Audio', icono: 'ri-mic-line' },
    { tipo: 'emoji', label: 'Emoji', icono: 'ri-emotion-line' },
    { tipo: 'imagen', label: 'Imagen', icono: 'ri-image-line' },
  ];

  const destinoPills: { destino: DestinoSaludo | 'todos'; label: string }[] = [
    { destino: 'todos', label: 'Todo' },
    { destino: 'radio', label: 'Radio' },
    { destino: 'programa', label: 'Programa' },
    { destino: 'locutor', label: 'Locutor' },
    { destino: 'evento', label: 'Evento' },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            SISTEMA DE SALUDOS
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Saludá, Dedicá y Conectá
          </h2>
          <p className="text-foreground-500 text-sm max-w-[620px] mx-auto leading-relaxed">
            Enviá saludos con texto, audio, emoji o imagen. A radios, programas, locutores o eventos. Seguí tu saludo desde Recibido hasta Al Aire. Todo dentro de Cabina.
          </p>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Saludos Hoy', valor: globalStatsSaludos.saludosHoy.toString(), icono: 'ri-message-3-line', color: 'text-rose-600', bg: 'bg-rose-100' },
            { label: 'Al Aire', valor: globalStatsSaludos.saludosAlAire.toString(), icono: 'ri-broadcast-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
            { label: 'Destacados', valor: globalStatsSaludos.saludosDestacados.toString(), icono: 'ri-star-line', color: 'text-amber-600', bg: 'bg-amber-100' },
            { label: 'Score Generado', valor: globalStatsSaludos.scoreTotalGenerado.toLocaleString(), icono: 'ri-flashlight-line', color: 'text-violet-600', bg: 'bg-violet-100' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${stat.icono} ${stat.color} text-sm`}></i>
              </div>
              <p className="text-foreground-950 font-bold text-lg">{stat.valor}</p>
              <p className="text-foreground-400 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tipos mini breakdown */}
        <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
          <span className="text-foreground-400 text-[10px] flex items-center gap-1">
            <i className="ri-text text-xs"></i>{globalStatsSaludos.tiposTexto} texto
          </span>
          <span className="text-foreground-400 text-[10px] flex items-center gap-1">
            <i className="ri-mic-line text-xs"></i>{globalStatsSaludos.tiposAudio} audio
          </span>
          <span className="text-foreground-400 text-[10px] flex items-center gap-1">
            <i className="ri-emotion-line text-xs"></i>{globalStatsSaludos.tiposEmoji} emoji
          </span>
          <span className="text-foreground-400 text-[10px] flex items-center gap-1">
            <i className="ri-image-line text-xs"></i>{globalStatsSaludos.tiposImagen} imagen
          </span>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5 mb-4 flex-wrap">
          {([
            { id: 'al-aire', icono: 'ri-broadcast-line', label: 'Al Aire' },
            { id: 'mas-compartidos', icono: 'ri-share-forward-line', label: 'Más Compartidos' },
            { id: 'destacados', icono: 'ri-star-line', label: 'Destacados' },
            { id: 'acciones', icono: 'ri-flashlight-line', label: 'Score' },
            { id: 'reglas', icono: 'ri-scales-line', label: 'Reglas' },
          ] as { id: TabSaludos; icono: string; label: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
              }`}
            >
              <i className={tab.icono}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'acciones' ? (
          <div className="max-w-[700px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {scoreAccionesSaludos.map((acc) => (
                <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${acc.icono} text-background-50 text-lg`}></i>
                  </div>
                  <p className="text-foreground-950 font-bold text-sm mb-1">{acc.accion}</p>
                  <p className="text-foreground-400 text-xs leading-relaxed mb-3">{acc.descripcion}</p>
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold">
                    <i className="ri-add-line"></i>+{acc.puntos} pts
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : activeTab === 'reglas' ? (
          <div className="max-w-[900px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {reglasSaludos.map((regla) => (
                <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                      <i className={`${regla.icono} text-background-50 text-sm`}></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-950 font-bold text-sm mb-1">{regla.titulo}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Saludos grid */
          <div>
            {/* Filters */}
            <div className="flex items-center justify-center gap-2 mb-5 flex-wrap">
              <div className="flex items-center gap-1">
                {tipoPills.map((pill) => (
                  <button
                    key={pill.tipo}
                    onClick={() => setTipoFiltro(pill.tipo)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      tipoFiltro === pill.tipo
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    <i className={`${pill.icono} text-[10px]`}></i>
                    {pill.label}
                  </button>
                ))}
              </div>
              <span className="text-foreground-300 text-[10px]">|</span>
              <div className="flex items-center gap-1">
                {destinoPills.map((pill) => (
                  <button
                    key={pill.destino}
                    onClick={() => setDestinoFiltro(pill.destino)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      destinoFiltro === pill.destino
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(activeTab === 'al-aire' ? alAire : activeTab === 'destacados' ? destacados : masCompartidos)
                .filter((s) => {
                  if (tipoFiltro !== 'todos' && s.tipo !== tipoFiltro) return false;
                  if (destinoFiltro !== 'todos' && s.destino !== destinoFiltro) return false;
                  return true;
                })
                .slice(0, 6)
                .map((s) => {
                  const tipoInfo = tipoLabels[s.tipo];
                  const estadoInfo = estadoLabelsSaludo[s.estado];
                  const destInfo = destinoLabels[s.destino];
                  return (
                    <div key={s.id} className="bg-background-50 rounded-2xl border border-background-200 p-4 hover:border-background-300 transition-colors">
                      {/* Tipo + Estado */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${tipoInfo.color} text-[9px] font-semibold`}>
                          <i className={`${tipoInfo.icono} text-[9px]`}></i>
                          {tipoInfo.label}
                        </span>
                        <span className="flex items-center gap-1 text-[9px]">
                          <span className={`w-1.5 h-1.5 rounded-full ${estadoInfo.dot}`}></span>
                          <span className="text-foreground-500">{estadoInfo.label}</span>
                        </span>
                      </div>

                      {/* Emoji display */}
                      {s.tipo === 'emoji' && s.emoji && (
                        <div className="text-2xl mb-2">{s.emoji}</div>
                      )}

                      {/* Audio indicator */}
                      {s.tipo === 'audio' && s.audioDuracion && (
                        <div className="flex items-center gap-2 mb-2 bg-violet-50 rounded-lg px-3 py-2">
                          <div className="w-8 h-8 rounded-lg bg-violet-200 flex items-center justify-center">
                            <i className="ri-mic-fill text-violet-600 text-sm"></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className="w-0.5 bg-violet-400 rounded-full animate-pulse" style={{ height: `${6 + i * 4}px`, animationDelay: `${i * 0.15}s` }}></span>
                              ))}
                            </div>
                            <p className="text-violet-500 text-[9px] mt-0.5">{s.audioDuracion}</p>
                          </div>
                        </div>
                      )}

                      {/* Text */}
                      <p className="text-foreground-600 text-[11px] leading-relaxed line-clamp-3 mb-3">
                        &ldquo;{s.texto}&rdquo;
                      </p>

                      {/* Remitente + Destino */}
                      <div className="flex items-center gap-2 mb-2">
                        <img
                          src={s.remitenteAvatar}
                          alt={s.remitente}
                          className="w-5 h-5 rounded-full object-cover"
                          loading="lazy"
                        />
                        <span className="text-foreground-600 text-[10px] font-medium">{s.remitente}</span>
                        <span className="text-foreground-400 text-[9px]">{s.remitenteCiudad}</span>
                      </div>
                      <div className="flex items-center gap-1 mb-2">
                        <i className={`${destInfo.icono} text-foreground-400 text-[9px]`}></i>
                        <span className="text-foreground-400 text-[9px]">{destInfo.label}: {s.destinoNombre}</span>
                      </div>

                      {/* Stats footer */}
                      <div className="flex items-center gap-3 pt-2 border-t border-background-100">
                        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
                          <i className="ri-share-forward-line"></i>{s.compartidos}
                        </span>
                        <span className="text-emerald-700 text-[9px] font-semibold ml-auto flex items-center gap-0.5">
                          <i className="ri-flashlight-fill"></i>{s.score} pts
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/sistema-saludos"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-amber-600 transition-all whitespace-nowrap"
          >
            <i className="ri-message-3-line"></i>
            Ver Todos los Saludos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-foreground-400 text-xs italic">
            &ldquo;Todo saludo vive dentro de Cabina. Enviar, leer, destacar y mover al aire — la interacción emocional sucede en un solo lugar.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}